import {Injectable} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {
  ATTACHMENTS_ACTIONS,
  ATTACHMENT_ACTION_TYPES,
  AttachmentType,
  DialogWidth,
  IMPACT_STATUSES,
  NoteAttachment,
  REF_MODEL_STORE,
  RefModelNode,
  RefModelStoreService,
  Refmodel,
  SnackBarService,
  TakeUntilComponentDestroy,
  UrlAttachment,
  UrlLibraryEntity,
  constants
} from '@q9elements/ui-kit/common';
import {RefModelSidebarService} from '@q9elements/ui-kit/refmodels';

import * as _ from 'lodash';
import {get, isEqual, omit} from 'lodash';
import {BehaviorSubject, Subject} from 'rxjs';
import {distinctUntilChanged, filter, map, switchMap, tap} from 'rxjs/operators';
import {TreeNode} from '../../models/rme/tree-node';

import {
  AttachmentsDialogsService,
  BaseAttachmentPayload,
  UrlAttachmentsDialogActions,
  UrlAttachmentsDialogOnSubmitPayload
} from '@q9elements/ui-kit/attachments';
import {AddRequirementDialogComponent} from '@q9elements/ui-kit/changes/requirements';
import {AddChangeDialogParams} from '@q9elements/ui-kit/changes/shared';
import {AddStoryDialogComponent} from '@q9elements/ui-kit/changes/stories';
import {ConfirmModalDialogComponent} from '@q9elements/ui-kit/components';
import {RefModelAttachmentsDaoService} from '@q9elements/ui-kit/refmodels';
import {environment} from '../../../environments/environment';
import {RunCodeSearchDialogComponent} from '../../code-search/components/run-code-search-dialog/run-code-search-dialog.component';
import {CreateNodeDialogComponent} from '../../editor-interface/components/create-node-dialog/create-node-dialog.component';
import {
  actionsToTriggerTreeReload,
  updateNodesDialogTitles
} from '../../editor-interface/components/update-tags-dialog/models/update-tags.interface';
import {UpdateTagsDialogComponent} from '../../editor-interface/components/update-tags-dialog/update-tags-dialog.component';
import {NewNodeTypes} from '../../editor-interface/models/enums/NodeTypes.enum';
import {AttachNodeService} from '../../editor-interface/services/attach-node.service';
import {AttachmentDialogTypesEnum} from '../models/context-menu.enum';

@Injectable()
export class ContextMenuActionsService extends TakeUntilComponentDestroy {
  public proposedNodeCreatingDialogEmit$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public updateNodeGdprStatusInTreeEmit$: BehaviorSubject<any> = new BehaviorSubject<any>([]);
  public makeNodeRootEmit$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public addNewNodeEmit$: BehaviorSubject<any> = new BehaviorSubject<any>({});
  public nodesBulkUpdatedEmitter$: Subject<any[]> = new Subject<any[]>();
  private selectedNode: RefModelNode;
  private refModel: Refmodel;
  private copiedOrgModelNode: any;
  private refModelId: string;
  private nodeId: string;
  private storeRefModelId = this.rmeStore.refModel._id;

  constructor(
    private router: Router,
    private rmeStore: RefModelStoreService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private attachmentsDialogsService: AttachmentsDialogsService,
    private refModelAttachmentsDaoService: RefModelAttachmentsDaoService,
    private snackBarService: SnackBarService,
    private attachNodeService: AttachNodeService,
    private refModelSidebarService: RefModelSidebarService
  ) {
    super();

    this.initRefModelStoreSubscription();
    this.initSelectedNodeStoreSubscription();
    this.attachNodeService.checkCanPasteNode();
    this.setCookieOrgNode();
  }

  private setCookieOrgNode(): boolean {
    const nodeCache = this.attachNodeService.getNodeFromCookie(constants.cookie.copiedSFNodeKey);

    if (!nodeCache) {
      return false;
    }

    this.copiedOrgModelNode = JSON.parse(nodeCache);
  }

  private initRefModelStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.REF_MODEL)
      .pipe(this.takeUntilComponentDestroy(), filter(Boolean))
      .subscribe((data: Refmodel) => {
        this.refModel = data;
      });
  }

  private initSelectedNodeStoreSubscription(): void {
    this.rmeStore
      .select(REF_MODEL_STORE.SELECTED_NODE)
      .pipe(
        this.takeUntilComponentDestroy(),
        distinctUntilChanged((prev, curr) => isEqual(prev, curr))
      )
      .subscribe((node: RefModelNode) => {
        this.selectedNode = Object.assign({}, node);
        this.refModelId = node?.refModel;
        this.nodeId = node?._id;
      });
  }

  public emitMakeNodeRoot(dataItem) {
    this.makeNodeRootEmit$.next(dataItem);
  }

  public openDependencyTree(nodeId: string) {
    window.open(
      `${environment.REF_MODEL_APP}${constants.impactAssessmentLink}${this.refModel._id}/${nodeId}`,
      '_blank'
    );
  }

  public showComponentInMetadataDictionary(nodeId: string) {
    window.open(
      `${environment.REF_MODEL_APP}/refmodel/${this.refModel._id}?node=${nodeId}`,
      '_blank'
    );
  }

  private getNestedValue(obj: TreeNode, path: string) {
    return path.split('.').reduce((acc, key) => acc?.[key], obj);
  }

  public openAttachmentDialog(nodeData: TreeNode, type, idField = 'id') {
    const getDataForAddRecordsDialog = (payload: BaseAttachmentPayload, attachmentId: string) => {
      const baseUrl = `/refmodel/${this.storeRefModelId}/node/${this.rmeStore.selectedNodeId}`;
      const {title, groups} = payload;
      const data = {
        title,
        groups,
        viewersCanAddRecords: false,
        userCanAddRecords: true,
        isPreview: false,
        urlConfig: {
          attachmentId: attachmentId,
          attachment: `${baseUrl}/attachment`,
          records: `${baseUrl}/attachment/${attachmentId}/records`,
          record: `${baseUrl}/attachment/${attachmentId}/record`
        }
      };

      return {...data, _id: attachmentId};
    };

    const onSubmit = (payload: BaseAttachmentPayload) => {
      if (type === AttachmentDialogTypesEnum.TEXT) {
        const {title, type, content, groups, noteProItems} = payload;
        payload = {
          title,
          type,
          value: content,
          groups,
          noteProItems
        };
      }

      if (type === AttachmentDialogTypesEnum.DATA_TABLE) {
        payload.type = AttachmentType.DATA_TABLE;
      }

      if (type === AttachmentDialogTypesEnum.IMAGE) {
        payload = omit(payload, 'blob');
      }

      return this.refModelAttachmentsDaoService
        .createRefModelNodeAttachment(this.storeRefModelId, this.rmeStore.selectedNodeId, payload)
        .pipe(
          map(response => {
            if (payload.type === AttachmentType.DATA_TABLE) {
              return getDataForAddRecordsDialog(payload, response._id);
            }

            return response;
          }),
          tap(() => {
            this.refModelSidebarService.triggerSidebarReload();
            this.triggerReloadTree();
          })
        );
    };

    switch (type) {
      /** Documentation */
      case AttachmentDialogTypesEnum.IMAGE:
        this.attachmentsDialogsService.openCreateImageAttachmentDialog(
          ATTACHMENT_ACTION_TYPES.REFMODEL_ATTACHMENT,
          (action: string, payload: BaseAttachmentPayload) => onSubmit(payload)
        );
        break;
      case AttachmentDialogTypesEnum.TEXT:
        const data = {
          params: {
            isPreview: false,
            itemType: 'text'
          },
          onSubmit: (action: ATTACHMENTS_ACTIONS, data: NoteAttachment) => {
            return onSubmit(data);
          }
        };

        this.attachmentsDialogsService.openNoteAttachmentDialog(data);
        break;
      case AttachmentDialogTypesEnum.DATA_TABLE:
        this.attachmentsDialogsService
          .openCreateDataTableAttachmentDialog(onSubmit, {
            nodeId: this.getNestedValue(nodeData, idField)
          })
          .pipe(
            switchMap(data =>
              this.attachmentsDialogsService.openDataTableRecordsDialog(data, this.refModelId)
            )
          )
          .subscribe();
        break;
      case AttachmentDialogTypesEnum.URL:
        this.addUrlAttachment();
        break;
      case AttachmentDialogTypesEnum.ORG_MODEL_NODE:
        this.setCookieOrgNode();
        this.attachNodeService.pasteDependency({
          refModelId: this.refModelId,
          nodeId: this.nodeId,
          copiedNode: this.copiedOrgModelNode,
          selectedNode: this.selectedNode
        });
        break;
    }
  }

  public openAddRequirementDialog() {
    const nodeId = this.rmeStore.selectedNodeId;
    const onSubmit: AddChangeDialogParams['onSubmit'] = ({items, groups}) => {
      const links = items.map(requirement => ({
        reqId: requirement.reqId,
        requirement: requirement.id,
        title: requirement.summary.substring(80, 0),
        description: requirement.description || '',
        reqReason: requirement.reqReason || '',
        reqDetails: requirement.details || '',
        groups: groups
      }));

      return this.refModelAttachmentsDaoService.attachChangesToNode(
        this.storeRefModelId,
        nodeId,
        links,
        AttachmentDialogTypesEnum.REQUIREMENT
      );
    };

    return this.dialog
      .open(AddRequirementDialogComponent, {
        width: DialogWidth.LG,
        data: {
          onSubmit,
          requestParams: {
            nodeId
          },
          groupsSelect: true
        }
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.refModelSidebarService.triggerSidebarReload();
        this.refModelSidebarService.triggerNodeTreeReload();
      });
  }

  public openAddNodeDialog(selectedNode, type) {
    let parentId;

    if (type === NewNodeTypes.CHILD) {
      parentId = selectedNode.id;
    }
    if (type === NewNodeTypes.SIBLING) {
      parentId = selectedNode.parentId;
    }

    const dialogRef = this.dialog.open(CreateNodeDialogComponent, {
      width: '310px' /** Title is too long for default width */,
      panelClass: 'q9-dialog',
      data: {
        refModelId: this.refModel._id,
        selectedNode,
        parentId,
        type
      }
    });

    dialogRef
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(node => this.addNewNodeEmit$.next({type, node, parentId}));
  }

  public copyNodeLink(selectedTreeNode, idField = 'id') {
    if (this.selectedNode._id !== this.getNestedValue(selectedTreeNode, idField)) {
      return;
    }

    this.attachNodeService.copyNode();
  }

  public openProposedNodeCreatingDialog(dataItem, subType?: string) {
    const {id: parentId, childType} = dataItem;
    const dialogData = {parentId, type: subType || childType, refModelId: this.refModel._id};

    this.proposedNodeCreatingDialogEmit$.next(dialogData);
  }

  public openNodeBulkUpdateDialog(nodeIds: [], action: string, isDependency?: boolean) {
    this.dialog
      .open(UpdateTagsDialogComponent, {
        width: constants.dialog.width.md,
        disableClose: true,
        panelClass: ['q9-dialog'],
        data: {
          nodeIds,
          action,
          isDependency,
          title: this.translate.instant(updateNodesDialogTitles[action])
        }
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe((updatedNodes: any[]) => {
        this.refModelSidebarService.refModelNode.set(this.selectedNode);
        this.refModelSidebarService.triggerSidebarReload();

        if (actionsToTriggerTreeReload.includes(action)) {
          this.triggerReloadTree();
        }
        this.nodesBulkUpdatedEmitter$.next(updatedNodes);
      });
  }

  public openDependencyGrid(nodeIds: string[]) {
    return window.open(
      `${environment.REF_MODEL_APP}/impact-grid-view/${this.refModel._id}?nodes=${JSON.stringify(
        nodeIds
      )}`,
      '_blank'
    );
  }

  public attachMultipleStoryForCodeSearch(
    nodeIds: string[],
    codeSearchIds: string[],
    codeSearchType: string
  ) {
    const onSubmit: AddChangeDialogParams['onSubmit'] = ({items, groups}) => {
      const stories = items.map(item => item.id);
      const payload = {
        groups,
        stories,
        nodeIds,
        impactStatus: IMPACT_STATUSES.TO_DO,
        multipleStories: true,
        pickedFromLibrary: false,
        newlyCreatedStory: true,
        outcome: items[0].outcome,
        description: '',
        populateDescriptionFrom: codeSearchIds,
        populateDescriptionSource: codeSearchType
      };

      return this.refModelAttachmentsDaoService.bulkLinkNodesToChanges(payload);
    };

    const storyDialog = this.dialog.open(AddStoryDialogComponent, {
      width: DialogWidth.LG,
      data: {
        onSubmit,
        storiesAmount: nodeIds.length,
        disabledPicklist: true,
        groupsSelect: true
      } as AddChangeDialogParams
    });

    storyDialog
      .afterOpened()
      .pipe(filter(() => !!codeSearchIds.length))
      .subscribe(() => this.informAboutDescriptionPopulate());

    return storyDialog
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(data => {
        const {added, requested} = data;
        const msg = this.translate.instant('CORE.STORIES.MULTIPLE_STORIES_CREATED', {
          added,
          requested
        });

        return this.snackBarService.success(msg);
      });
  }

  public findStory(nodeIds: any[]) {
    const onSubmit: AddChangeDialogParams['onSubmit'] = ({items, groups}) => {
      const stories = items.map(item => item.id);
      const payload = {
        groups,
        stories,
        nodeIds,
        impactStatus: IMPACT_STATUSES.TO_DO,
        multipleStories: true,
        pickedFromLibrary: true,
        newlyCreatedStory: false,
        populateDescriptionFrom: [],
        populateDescriptionSource: ''
      };

      return this.refModelAttachmentsDaoService.bulkLinkNodesToChanges(payload);
    };
    const isMultipleNodesSelected = nodeIds.length > 1;

    this.dialog
      .open(AddStoryDialogComponent, {
        width: DialogWidth.LG,
        data: {
          onSubmit,
          disabledCreate: true,
          groupsSelect: true,
          ...(isMultipleNodesSelected && {maxPickedItems: 5})
        } as AddChangeDialogParams
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(({added}) => {
        const msg = this.translate.instant('CORE.STORIES.STORY_TO_NODE_LINKED', {
          added
        });
        this.refModelSidebarService.triggerSidebarReload();
        this.triggerReloadTree();

        if (_.isNumber(added)) {
          return this.snackBarService.success(msg);
        }
      });
  }

  public createAndAttachStory(nodeIds: string[], multipleStories = false) {
    const onSubmit: AddChangeDialogParams['onSubmit'] = ({items, groups}) => {
      const stories = items.map(item => item.id);
      const payload = {
        groups,
        stories,
        nodeIds,
        impactStatus: IMPACT_STATUSES.TO_DO,
        multipleStories: multipleStories,
        pickedFromLibrary: false,
        newlyCreatedStory: true,
        outcome: items[0].outcome,
        description: '',
        populateDescriptionFrom: [],
        populateDescriptionSource: ''
      };

      return this.refModelAttachmentsDaoService.bulkLinkNodesToChanges(payload);
    };

    this.dialog
      .open(AddStoryDialogComponent, {
        width: DialogWidth.LG,
        data: {
          onSubmit,
          storiesAmount: multipleStories ? nodeIds.length : 0,
          disabledPicklist: true,
          groupsSelect: true
        } as AddChangeDialogParams
      })
      .afterClosed()
      .pipe(filter(Boolean))
      .subscribe(data => {
        this.refModelSidebarService.triggerSidebarReload();
        if (!multipleStories) {
          this.triggerReloadTree();
        }
        const messageKey = multipleStories
          ? 'CORE.STORIES.MULTIPLE_STORIES_CREATED'
          : 'CORE.STORIES.MULTIPLE_NODES_ADD';
        const {added, requested} = data;
        const msg = this.translate.instant(messageKey, {
          added,
          requested
        });

        return this.snackBarService.success(msg);
      });
  }

  public openCodeSearchDialog(dataItem) {
    this.dialog
      .open(RunCodeSearchDialogComponent, {
        width: constants.dialog.width.lg,
        disableClose: true,
        panelClass: ['q9-dialog'],
        data: {
          node: dataItem,
          title: this.translate.instant('CODE_SEARCH.RUN_CODE_SEARCH_DIALOG_TITLE')
        }
      })
      .afterClosed()
      .subscribe();
  }

  public resetEmitters() {
    this.proposedNodeCreatingDialogEmit$.next({});
    this.updateNodeGdprStatusInTreeEmit$.next([]);
    this.addNewNodeEmit$.next({});
    this.makeNodeRootEmit$.next({});
  }

  public addUrlAttachment() {
    const nodeId = this.rmeStore.selectedNodeId;

    const onSubmit = (action: string, payload: UrlAttachmentsDialogOnSubmitPayload) => {
      if (action === UrlAttachmentsDialogActions.ATTACH_URL) {
        const urlRecords = get(payload, 'records', []);
        const attachedUrls = urlRecords.map(record => ({
          type: 'url_entry',
          urlEntry: record.id
        }));
        const formattedPayload = {
          ...(payload as {records: UrlLibraryEntity[]; groups: string[]; type: string}),
          records: attachedUrls
        };

        return this.refModelAttachmentsDaoService.attachMultipleUrl(
          this.storeRefModelId,
          nodeId,
          formattedPayload
        );
      }

      return this.refModelAttachmentsDaoService.createRefModelNodeAttachment(
        this.storeRefModelId,
        nodeId,
        payload as BaseAttachmentPayload
      );
    };

    const attachmentList = this.selectedNode.generalAttachments.filter(
      entity => entity.type === AttachmentType.URL_ENTRY
    ) as unknown as UrlAttachment[];
    const queryParams = {nodeId: this.selectedNode._id};

    return this.attachmentsDialogsService
      .openAttachUrlDialog(queryParams, attachmentList, onSubmit)
      .pipe(filter(Boolean))
      .subscribe(() => {
        this.refModelSidebarService.triggerSidebarReload();
      });
  }

  private informAboutDescriptionPopulate() {
    return this.dialog.open(ConfirmModalDialogComponent, {
      width: DialogWidth.MD,
      disableClose: false,
      data: {
        modalTitle: 'STORIES.POPULATE_DESCRIPTION_POPUP_TITLE',
        content: 'STORIES.POPULATE_DESCRIPTION_MESSAGE',
        showCancelButton: false,
        confirmText: 'CONFIRM_DIALOG.CONFIRM_DEFAULT'
      }
    });
  }

  private triggerReloadTree(): void {
    const excludedPaths = [
      '/customView',
      '/code-search',
      '/impact-grid-view',
      '/sf-impact-assessment'
    ];
    const shouldReload = !excludedPaths.some(path => this.router.url.includes(path));

    if (shouldReload) {
      this.refModelSidebarService.triggerNodeTreeReload();
    }
  }
}
